'use client';
/**
 * Socle de contrôle des formulaires — le bandeau, le saut, et les champs vides.
 *
 * Demande de la RQ le 14/08/2026, constatée sur la fiche d'équipement et
 * demandée « pour tous les formulaires » :
 *
 *  1. repérer d'un coup d'œil un champ pas rempli du tout ;
 *  2. quand un champ obligatoire manque, y ÊTRE EMMENÉ — le rouge ne sert à
 *     rien si le champ est huit cents pixels plus haut que le bouton ;
 *  3. un rappel en haut qui NOMME les champs manquants ou erronés.
 *
 * La logique est ailleurs, dans `@/lib/formulaire` — module pur, testé par
 * `backend/tests/qualite-formulaire.test.js`. Ici, il n'y a que du DOM.
 */
import React, { useCallback, useEffect, useMemo, useRef } from 'react';
import { problemes as calculerProblemes, champsVides, resume, type ChampDeclare, type Probleme } from '@/lib/formulaire';
import { I } from '@/components/ui';

/**
 * Amène à un champ et lui donne le clavier.
 *
 * `block: 'center'` et non `'start'` : l'application porte un bandeau collant
 * en haut ET, sur les écrans maître-détail, des colonnes à défilement propre.
 * Aligner sur le haut plaçait le champ SOUS le bandeau — on sautait au bon
 * endroit et on ne voyait rien. Centrer traverse les deux cas sans les
 * connaître.
 *
 * Le `focus` vient après le défilement, dans un `requestAnimationFrame` : donné
 * avant, le navigateur refait son propre défilement (brutal, aligné en haut) et
 * défait celui qu'on vient de faire.
 */
export function sauterVers(id: string): void {
  if (typeof document === 'undefined') return;
  const el = document.getElementById(id) as HTMLElement | null;
  if (!el) return;
  el.scrollIntoView({ behavior: 'smooth', block: 'center' });
  requestAnimationFrame(() => {
    try { (el as HTMLInputElement).focus({ preventScroll: true }); } catch { el.focus(); }
  });
}

/**
 * Rappel des problèmes, en tête de formulaire.
 *
 * `role="alert"` : les lecteurs d'écran l'annoncent dès son apparition, sans
 * quoi un utilisateur au clavier resterait devant un bouton qui « ne fait
 * rien ». Chaque ligne est un BOUTON qui emmène au champ : lire ce qui manque
 * sans pouvoir y aller obligerait à le chercher à l'œil, ce qui est justement
 * le geste qu'on supprime.
 */
export function RappelProblemes({ problemes, onFermer }: {
  problemes: Probleme[];
  onFermer?: () => void;
}) {
  if (!problemes.length) return null;
  return (
    <div role="alert"
      style={{
        display: 'grid', gap: 8, padding: '12px 14px', marginBottom: 12,
        background: 'var(--red-50, #FDECEA)', border: '1px solid var(--red-200, #F1C9C7)',
        borderRadius: 'var(--radius-md)',
      }}>
      <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
        <I n="alert" s={15} />
        <strong style={{ fontSize: 13, color: 'var(--red-700, #B3261E)' }}>{resume(problemes)}</strong>
        <span style={{ flex: 1 }} />
        {onFermer && (
          <button type="button" onClick={onFermer} aria-label="Fermer le rappel"
            style={{ background: 'transparent', border: 'none', color: 'var(--red-700, #B3261E)', cursor: 'pointer', padding: 2 }}>
            <I n="x" s={14} />
          </button>
        )}
      </div>
      <ul style={{ margin: 0, padding: 0, listStyle: 'none', display: 'grid', gap: 4 }}>
        {problemes.map((p) => (
          <li key={p.id}>
            <button type="button" onClick={() => sauterVers(p.id)}
              title="Aller à ce champ"
              style={{
                display: 'inline-flex', alignItems: 'center', gap: 6, background: 'transparent',
                border: 'none', padding: 0, cursor: 'pointer', fontFamily: 'var(--font-sans)',
                fontSize: 12.5, color: 'var(--red-700, #B3261E)', textAlign: 'start',
              }} className="lien-doux">
              {p.message}
            </button>
          </li>
        ))}
      </ul>
    </div>
  );
}

/**
 * Le contrôle d'un formulaire : ce qui manque, où sauter, et ce qui est vide.
 *
 * ── Le marquage des champs vides ────────────────────────────────────────────
 *
 * Il ne se fait PAS en CSS. On y a pensé — `:placeholder-shown` couvrirait les
 * champs qui portent une indication —, mais il en existe sans, et surtout les
 * listes déroulantes et les zones de texte y échappent. Un marquage qui couvre
 * « la plupart » des champs est pire que pas de marquage : on finit par croire
 * qu'un champ non marqué est rempli.
 *
 * On pose donc un attribut `data-vide` sur les éléments réellement vides, après
 * chaque rendu, en lisant leur valeur dans le DOM. C'est fiable pour tous les
 * types de champ, et la feuille de style n'a plus qu'à le teinter.
 */
export function useFormulaire(champs: ChampDeclare[]) {
  const ref = useRef<HTMLDivElement | null>(null);
  const [montrer, setMontrer] = React.useState(false);

  const problemes = useMemo(() => calculerProblemes(champs), [champs]);
  const vides = useMemo(() => champsVides(champs), [champs]);

  // Le marquage est recalculé à CHAQUE rendu : un champ qu'on vient de remplir
  // doit cesser d'être marqué tout de suite, sinon le repère devient du bruit
  // et on cesse de le regarder.
  useEffect(() => {
    const racine = ref.current;
    if (!racine) return;
    const aMarquer = new Set(vides);
    racine.querySelectorAll('[id]').forEach((el) => {
      const id = el.getAttribute('id') || '';
      if (aMarquer.has(id)) el.setAttribute('data-vide', '1');
      else el.removeAttribute('data-vide');
    });
    // Les champs en défaut portent leur propre marque : elle est plus forte que
    // « vide », et les deux ne doivent pas se superposer sur le même champ.
    const enDefaut = new Set(problemes.map((p) => p.id));
    racine.querySelectorAll('[id]').forEach((el) => {
      const id = el.getAttribute('id') || '';
      if (montrer && enDefaut.has(id)) {
        el.setAttribute('data-defaut', '1');
        el.setAttribute('aria-invalid', 'true');
        el.removeAttribute('data-vide');
      } else {
        el.removeAttribute('data-defaut');
        el.removeAttribute('aria-invalid');
      }
    });
  });

  /**
   * À appeler AVANT d'enregistrer. Rend `true` si le formulaire peut partir.
   *
   * Quand il ne le peut pas : le bandeau s'affiche, les champs fautifs se
   * marquent, et on est EMMENÉ au premier — celui du haut. C'est le geste que
   * la RQ réclamait : sans lui, le clic sur « Enregistrer » depuis le bas d'une
   * fiche longue ne produisait rien de visible.
   */
  const verifier = useCallback((): boolean => {
    if (!problemes.length) { setMontrer(false); return true; }
    setMontrer(true);
    const premier = problemes[0];
    if (premier) sauterVers(premier.id);
    return false;
  }, [problemes]);

  const Bandeau = useCallback(
    () => (montrer ? <RappelProblemes problemes={problemes} onFermer={() => setMontrer(false)} /> : null),
    [montrer, problemes],
  );

  return { ref, verifier, problemes, Bandeau, montre: montrer };
}
